import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { ROUTE_PATHS } from '../../routes/routePaths';
import { PREVIOUS_LOCATION_KEY } from '../../util/constants';

const EurlNavigationGuard = () => {
  const location = useLocation();

  const previousLocationRef = useRef<string>(
    sessionStorage.getItem(PREVIOUS_LOCATION_KEY) ?? ''
  );

  // Track if we're currently redirecting to prevent infinite loops
  const isRedirectingRef = useRef<boolean>(false);

  const isFirstRenderRef = useRef<boolean>(true);

  useEffect(() => {
    if (isRedirectingRef.current) {
      isRedirectingRef.current = false;
      const newLocation = location.pathname + location.search;
      previousLocationRef.current = newLocation;
      sessionStorage.setItem(PREVIOUS_LOCATION_KEY, newLocation);
      return;
    }

    const searchParameters = new URLSearchParams(location.search);
    const hasEurl = searchParameters.has('eurl') || searchParameters.has('eURL');
    const eurlValue = searchParameters.get('eurl') ?? searchParameters.get('eURL') ?? '';
    const isEurlEmpty = eurlValue.trim() === '';

    const currentLocation = location.pathname + location.search;
    const previousLocation = previousLocationRef.current;

    if (isFirstRenderRef.current) {
      isFirstRenderRef.current = false;
      if (!previousLocation) {
        previousLocationRef.current = currentLocation;
        sessionStorage.setItem(PREVIOUS_LOCATION_KEY, currentLocation);
      }
    }

    // Check if previous location had eurl and extract its pathname
    const previousLocationParts = previousLocation.split('?');
    const previousPathname = previousLocationParts[0] ?? '';
    const previousSearchParameters = new URLSearchParams(previousLocationParts[1] ?? '');
    const previousHadEurl = previousSearchParameters.has('eurl') || previousSearchParameters.has('eURL');
    const previousEurlValue = previousSearchParameters.get('eurl') ?? previousSearchParameters.get('eURL') ?? '';
    const previousEurlWasValid = previousHadEurl && previousEurlValue.trim() !== '';

    // Exclude report-view-compare page from validation (it doesn't require eurl)
    const isReportViewCompare = location.pathname === ROUTE_PATHS.REPORT_VIEW_COMPARE;

    // List of routes that require eURL parameter
    const routesRequiringEurl = [
      ROUTE_PATHS.PRODUCT,
      ROUTE_PATHS.PREVIEW_PAGES,
      ROUTE_PATHS.EDITORIAL_PAGE,
      ROUTE_PATHS.REPORT_TAB,
      ROUTE_PATHS.REPORT_SINGLE,
      ROUTE_PATHS.REPORT_VIEW,
      ROUTE_PATHS.VERSION_HISTORY,
      ROUTE_PATHS.PAGE_VERSION_HISTORY,
      ROUTE_PATHS.VIEW_GRID,
      ROUTE_PATHS.VIEW_GRID_FILTER,
      ROUTE_PATHS.CONTENT_TILE_VIEW_GRID,
      ROUTE_PATHS.GRID_SETTINGS,
      ROUTE_PATHS.GRID_VERSION_HISTORY
    ];

    const requiresEurl = routesRequiringEurl.includes(location.pathname);

    // Case 1: User removed eurl from URL or made it empty on a route that requires it
    // AND previous location had valid eurl on the same route
    if (
      !isReportViewCompare &&
      requiresEurl &&
      previousEurlWasValid &&
      previousLocation &&
      location.pathname === previousPathname &&
      (!hasEurl || isEurlEmpty)
    ) {
      isRedirectingRef.current = true;
      window.location.replace(`${ROUTE_PATHS.STANDARD_ERROR}?invalidLink=true`);
      return;
    }

    // Case 2: Direct navigation to a route that requires eurl without eurl parameter
    // (handles first-time visits or direct URL access)
    if (
      !isReportViewCompare &&
      requiresEurl &&
      (!hasEurl || isEurlEmpty) &&
      !previousEurlWasValid
    ) {
      isRedirectingRef.current = true;
      window.location.replace(`${ROUTE_PATHS.STANDARD_ERROR}?invalidLink=true`);
      return;
    }

    // Update previous location ref for next iteration
    previousLocationRef.current = currentLocation;
    sessionStorage.setItem(PREVIOUS_LOCATION_KEY, currentLocation);
  }, [location]);

  return null;
};

export default EurlNavigationGuard;

